import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import { bulkAssignCrmUser, searchCrmAssignableStaff } from '../config/api';

const toOption = (user) => ({
  value: user.id,
  label: user.name || user.email || `Staff #${user.id}`,
  user,
});

const CrmBulkAssignUserDialog = ({ leadIds, onClose, onSaved }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadStaff = async (search) => {
    try {
      const response = await searchCrmAssignableStaff(search);
      const result = response?.data || response;
      return (result?.users || []).map(toOption);
    } catch (err) {
      setError(err.message || 'Could not load CRM staff.');
      return [];
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedUser) {
      setError('Select a CRM staff user.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await bulkAssignCrmUser(leadIds, selectedUser.value);
      if (onSaved) await onSaved(response?.data || response);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not assign the selected Leads.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()} style={{ maxWidth: '560px' }}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h3>Assign User</h3>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <div className="modal-body">
            <p className="crm-muted">
              Assign {leadIds.length} selected Lead{leadIds.length === 1 ? '' : 's'} to one CRM staff user.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="crm-bulk-assignee-search">CRM staff user</label>
              <AsyncSelect
                inputId="crm-bulk-assignee-search"
                cacheOptions
                defaultOptions
                loadOptions={loadStaff}
                value={selectedUser}
                onChange={(option) => {
                  setSelectedUser(option);
                  setError('');
                }}
                formatOptionLabel={(option) => (
                  <div className="crm-oms-user-option">
                    <strong>{option.label}</strong>
                    <span>{[option.user.email, option.user.role].filter(Boolean).join(' · ')}</span>
                  </div>
                )}
                placeholder="Search CRM staff by name or email"
                noOptionsMessage={() => 'No eligible CRM staff found'}
                isClearable
                isDisabled={saving}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 1100 }) }}
                autoFocus
              />
              <div className="crm-form-help">
                The selected Leads will appear in this user’s Calling View when follow-up is due.
              </div>
            </div>
            {error && <div className="crm-form-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !selectedUser}>
              {saving ? 'Assigning…' : 'Assign User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmBulkAssignUserDialog;
